import type { CityConfig } from "../city-template/types";

export const vallejoConfig: CityConfig = {
  city: "Vallejo",
  state: "California",
  stateAbbr: "CA",
  slug: "vallejo",
  route: "/cities/vallejo",
  vitalRecordsIcon: "ri-file-text-line",
  lastModified: "2026-06-14",
  title: "Emergency Passport Vallejo, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Vallejo, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Solano County and North Bay travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Vallejo, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Vallejo and Solano County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Vallejo%20California%20waterfront%20cityscape%20at%20golden%20hour%20with%20Mare%20Island%20historic%20buildings%2C%20San%20Pablo%20Bay%20shoreline%20with%20warm%20sunset%20light%20reflecting%20on%20calm%20water%2C%20downtown%20Vallejo%20hillside%20with%20modern%20homes%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20North%20Bay%20coastal%20city%20environment%20with%20warm%20amber%20and%20orange%20tones&width=1800&height=700&seq=vallejo-hero-001&orientation=landscape",
  heroImageAlt: "Vallejo California waterfront skyline at golden hour with Mare Island and San Pablo Bay shoreline",
  geoPosition: "38.1041;-122.2566",
  geoPlacename: "Vallejo, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in downtown SF — approximately 35–45 minutes from Vallejo via I-80",
  },
  nearbyCitiesZips: [
    { name: "Vallejo", zips: "94589–94592" },
    { name: "Fairfield", zips: "94533, 94534" },
    { name: "Vacaville", zips: "95687, 95688" },
    { name: "Benicia", zips: "94510" },
    { name: "Napa", zips: "94558, 94559" },
    { name: "American Canyon", zips: "94503" },
    { name: "Suisun City", zips: "94585" },
    { name: "Dixon", zips: "95620" },
    { name: "Rio Vista", zips: "94571" },
    { name: "Petaluma", zips: "94952, 94954" },
    { name: "Sonoma", zips: "95476" },
    { name: "Elk Grove", zips: "95624, 95757, 95758" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Solano County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "Oakland", route: "/cities/oakland" },
    { name: "Berkeley", route: "/cities/berkeley" },
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Santa Rosa", route: "/cities/santa-rosa" },
    { name: "Daly City", route: "/cities/daly-city" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Vallejo remotely via secure courier. We do not have a walk-in office in Vallejo — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Solano County travelers.",
  fpcOfficeAddress: "400 S El Camino Real, San Mateo, CA 94402",
  fpcOfficeDistance: "Serving all of Solano County",
  serviceAreaIntro: "From the downtown waterfront to Glen Cove, from the Crest to the Hillside — Fast Passport Center serves the entire Vallejo community. We regularly help travelers from Fairfield, Vacaville, Benicia, and Napa get their passports fast.",
  nearbyCitiesIntro: "We serve all Vallejo neighborhoods and nearby North Bay cities including Fairfield, Vacaville, Benicia, Napa, American Canyon, and Suisun City. If you are in Solano County or nearby Napa County, we have you covered.",
  howItWorksIntro: "Most Vallejo travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With San Francisco International Airport serving as the closest major international hub, North Bay travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Vallejo and Solano County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Vallejo? Here are the most common ones we hear from Solano County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Solano County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Diana M.",
      location: "Vallejo, CA",
      rating: 5,
      text: "My flight to the Philippines was in 2 days and my passport had expired. Fast Passport Center got me a new one in under 48 hours. Absolutely saved my family visit.",
      service: "Emergency Passport",
    },
    {
      name: "James K.",
      location: "Fairfield, CA",
      rating: 5,
      text: "Needed to renew for a cruise to Mexico. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Great service for North Bay residents.",
      service: "Passport Renewal",
    },
    {
      name: "Linda P.",
      location: "Vacaville, CA",
      rating: 5,
      text: "Lost my passport the week before my business trip to London. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Carlos R.",
      location: "Napa, CA",
      rating: 5,
      text: "As a frequent international traveler from the Wine Country, I can't afford passport delays. Fast Passport Center has been reliable every time. Highly recommend.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "vallejo-1",
      name: "Vallejo Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1725 Sacramento St",
      city: "Vallejo",
      state: "CA",
      zip: "94590",
      phone: "(707) 643-3217",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Vallejo",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "vallejo-2",
      name: "Fairfield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "415 Thermal Ct",
      city: "Fairfield",
      state: "CA",
      zip: "94533",
      phone: "(707) 425-0134",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Fairfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Fairfield residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "vallejo-3",
      name: "Vacaville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "333 Broadway St",
      city: "Vacaville",
      state: "CA",
      zip: "95688",
      phone: "(707) 448-1596",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.6 miles from downtown Vacaville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "vallejo-4",
      name: "Benicia Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "205 E D St",
      city: "Benicia",
      state: "CA",
      zip: "94510",
      phone: "(707) 745-3342",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Benicia",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Benicia and southern Solano County residents. Call to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "vallejo-5",
      name: "Napa Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1351 2nd St",
      city: "Napa",
      state: "CA",
      zip: "94559",
      phone: "(707) 252-2173",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Napa",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Napa County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "vallejo-6",
      name: "Solano County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "675 Texas St",
      city: "Fairfield",
      state: "CA",
      zip: "94533",
      phone: "(707) 784-6205",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from the Fairfield courthouse",
      website: "https://www.solanocounty.com/",
      websiteLabel: "Solano County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "North Bay photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Solano County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Vallejo applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Solano County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Solano County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Vallejo." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Vallejo address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Vallejo before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Vallejo for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Solano County travelers." },
    { q: "How fast can I renew my passport in Vallejo?", a: "Fast Passport Center offers fast passport renewal in Vallejo in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Vallejo in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Vallejo?", a: "Same-day passport appointments require a drive to the SF Regional Passport Agency. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Vallejo residents." },
    { q: "Can I expedite a child passport in Vallejo?", a: "Yes. Fast Passport Center expedites child passports in Vallejo for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Vallejo with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Vallejo?",
      a: "With Fast Passport Center's emergency courier service, Vallejo travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Vallejo?",
      a: "Popular options include the Vallejo Post Office (94590), Fairfield Post Office (94533), Vacaville Post Office (95688), and Solano County Clerk (94533). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Vallejo?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Fairfield, Vacaville, or Napa?",
      a: "We serve all of Solano County including Fairfield, Vacaville, Benicia, Suisun City, and Dixon. We also serve neighboring Napa County. Our overnight shipping labels work from any UPS location, so your location within the North Bay doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Vallejo?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Vallejo?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Vallejo, Fairfield, and Vacaville offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Vallejo?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Vallejo?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Solano County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Vallejo", address: "3015 Sonoma Blvd, Vallejo, CA 94590", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Vallejo", address: "1800 Tuolumne St, Vallejo, CA 94589", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Vallejo", address: "138 Plaza Dr, Vallejo, CA 94591", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Fairfield", address: "1891 Woolner Ave, Fairfield, CA 94533", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Vacaville", address: "1501 Helen Power Dr, Vacaville, CA 95687", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Napa", address: "1501 Trancas St, Napa, CA 94558", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Benicia", address: "102 E 2nd St, Benicia, CA 94510", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Fairfield", address: "2001 Nut Tree Rd, Fairfield, CA 94533", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Vallejo",
      address: "2001 Napa Rd, Vallejo, CA 94589",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Fairfield",
      address: "2001 Cadet Ct, Fairfield, CA 94533",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Vacaville",
      address: "620 E Monte Vista Ave, Vacaville, CA 95688",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Napa",
      address: "2555 Napa Valley Corporate Dr, Napa, CA 94558",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Napa County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "35 miles from Vallejo",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and worldwide destinations.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: true,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "25 miles from Vallejo",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Convenient for East Bay and North Bay travelers. Domestic and international flights.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "55 miles from Vallejo",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Major international airport for Northern California. Full international flight options.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Solano County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Solano County",
      url: "https://www.solanocounty.com/depts/countyclerk/",
    },
    {
      label: "Solano County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Solano County",
      url: "https://www.solano.courts.ca.gov/",
    },
  ],
};